/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * "Sorteren" on a phone (2026-10-08, composition B of the filter-bar mockup,
 * which Jasper's round picked for below sm). There the results bar is two
 * buttons — "Alle filters" (FilterSheet) and "Sorteren: <order>" — so that
 * filtering and sorting are two explicit acts and nothing hides behind a
 * scroller's fade (the one-row pill bar showed 153 of its 429 px at 375 px,
 * and the sort was the heaviest thing in it). The second button opens this
 * sheet: the sort control's own options, with its own semantics (a listbox,
 * the order in force selected, the arrows roving the options), as a sheet
 * instead of a menu under a 36 px button.
 *
 * How it behaves:
 *
 * - One tap applies: the engine sorts — or does nothing for the order already
 *   in force, so nothing is reported — and closes the sheet. A sort is one
 *   choice, so there is no draft and no apply button (FilterSheet edits
 *   several filters at once; this one does not).
 * - "Annuleren", Escape and a tap on the veil close it with nothing changed.
 * - The order in force takes the focus when it opens; the arrows, Home and End
 *   move it, Enter or Space picks. Tab goes from the options to "Annuleren"
 *   and back: the page is locked and the focus kept inside, and it goes back
 *   to the button that opened the sheet the moment it closes.
 *
 * Layout and lifecycle are FilterSheet's: `FILTER_SHEET_VIEWPORT_CLASS` on
 * `ModalViewport` (the bottom edge below sm, the centred card from sm), a new
 * panel per opening, ClientPortal — never in the bake.
 */

import React, { useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, m, useIsPresent } from 'motion/react';
import { Check } from 'lucide-react';
import { ClientPortal } from '../ClientPortal';
import { FILTER_SHEET_VIEWPORT_CLASS, MODAL_BACKDROP_CLASS, ModalViewport } from '../modalLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { cn } from '../../lib/cn';
import { lockPageScroll } from '../../lib/scrollLock';
import type { FilterOption } from './FilterSelect';

interface SortSheetProps {
  open: boolean;
  /** The sort control's options: the listbox shows each option's full label. */
  options: FilterOption[];
  /** The order in force. */
  value: string;
  /** A tap on an option. The engine sorts (when the order changes) and closes the sheet. */
  onSelect: (value: string) => void;
  /** "Annuleren", Escape or the veil: nothing changes. */
  onClose: () => void;
  /** Where the focus goes back to when the sheet closes — the button that opened it. */
  returnFocusRef: React.RefObject<HTMLElement | null>;
}

interface PanelProps extends Omit<SortSheetProps, 'open' | 'returnFocusRef'> {
  dialogRef: React.RefObject<HTMLDivElement | null>;
}

const SortSheetPanel: React.FC<PanelProps> = ({ options, value, onSelect, onClose, dialogRef }) => {
  const { t } = useLanguage();
  // False while the exit animation plays: the panel is no longer the visitor's.
  const isPresent = useIsPresent();
  const titleId = `${useId()}-title`;
  const listRef = useRef<HTMLDivElement>(null);
  // A sheet rises from the bottom edge; from sm the same panel is the centred
  // card. Read once, when the panel opens (client-only, as in FilterSheet).
  const [asSheet] = useState(
    () =>
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function' ||
      !window.matchMedia('(min-width: 640px)').matches
  );

  const optionButtons = () =>
    Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? []);

  // The arrows rove the options (with wrap), Home and End jump; nothing is
  // picked until Enter, Space or a tap — a pick closes the sheet.
  const onListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = optionButtons();
    if (!items.length) return;
    const idx = items.indexOf(document.activeElement as HTMLButtonElement);
    let next: number | null = null;
    if (e.key === 'ArrowDown') next = idx < 0 ? 0 : (idx + 1) % items.length;
    else if (e.key === 'ArrowUp')
      next = idx < 0 ? items.length - 1 : (idx - 1 + items.length) % items.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    if (next === null) return;
    e.preventDefault();
    items[next]?.focus();
  };

  const close = () => {
    if (isPresent) onClose();
  };

  return (
    <ModalViewport
      className={FILTER_SHEET_VIEWPORT_CLASS}
      inert={!isPresent}
      aria-hidden={isPresent ? undefined : true}
    >
      <m.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={close}
        className={MODAL_BACKDROP_CLASS}
      />
      <m.div
        // Set, never cleared: a panel animating out unmounts after the next
        // one has mounted (FilterSheet's note).
        ref={(el: HTMLDivElement | null) => {
          if (el) dialogRef.current = el;
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        initial={asSheet ? { y: '100%' } : { opacity: 0, y: 24, scale: 0.97 }}
        animate={asSheet ? { y: 0 } : { opacity: 1, y: 0, scale: 1 }}
        exit={
          asSheet
            ? { y: '100%', transition: { duration: 0.2, ease: 'easeIn' } }
            : { opacity: 0, y: 24, scale: 0.97, transition: { duration: 0.15 } }
        }
        transition={{ type: 'spring', damping: 34, stiffness: 340 }}
        className="relative flex max-h-[min(85svh,100%)] w-full flex-col overscroll-contain rounded-t-3xl border border-b-0 border-emerald-500/20 bg-slate-900 shadow-2xl shadow-emerald-900/30 outline-none sm:max-w-sm sm:rounded-3xl sm:border-b"
      >
        {/* The handle says "sheet" at a glance; it is a picture, not a control. */}
        <div className="shrink-0 touch-none border-b border-slate-700/60 px-5 pt-2.5 pb-3 sm:pt-4">
          <div
            aria-hidden="true"
            className="mx-auto mb-2.5 h-1.5 w-10 rounded-full bg-slate-600 sm:hidden"
          />
          <h2 id={titleId} className="text-lg font-extrabold text-white">
            {t('table.sort_label')}
          </h2>
        </div>

        <div
          ref={listRef}
          role="listbox"
          aria-labelledby={titleId}
          onKeyDown={onListKeyDown}
          className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain px-3 py-2"
        >
          {options.map((option) => {
            const selected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={selected}
                data-value={option.value}
                // One tab stop in the list: the order in force; the arrows reach the rest.
                tabIndex={selected ? 0 : -1}
                onClick={() => {
                  if (isPresent) onSelect(option.value);
                }}
                className={cn(
                  'flex min-h-12 w-full items-center justify-between gap-3 rounded-xl px-3 text-left text-[15px] font-semibold transition-colors focus-ring',
                  // The order in force carries a check as well as its colour
                  // (WCAG 1.4.1). Light: the literal emerald-800 hex on the tint
                  // (the emerald-300 token is #047857 there, 4.3:1 on it).
                  selected
                    ? 'bg-emerald-500/10 text-emerald-300 [html.light_&]:text-[#065f46]'
                    : 'text-slate-200 hover:bg-slate-800'
                )}
              >
                {option.label}
                {selected && <Check className="h-5 w-5 shrink-0" aria-hidden="true" />}
              </button>
            );
          })}
        </div>

        <div className="flex shrink-0 touch-none border-t border-slate-700/60 px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-4">
          <button
            type="button"
            onClick={close}
            className="h-11 flex-1 rounded-xl border border-slate-700 px-4 text-sm font-bold text-slate-200 transition-colors hover:bg-slate-800 focus-ring"
          >
            {t('filters.sheet_cancel')}
          </button>
        </div>
      </m.div>
    </ModalViewport>
  );
};

/**
 * Tab in the sheet, where the listbox is one stop. Any option — also one the
 * arrows moved to, which is no tab stop of its own — leaves the list for
 * "Annuleren" on Tab and wraps there on Shift+Tab (the list comes first); from
 * "Annuleren" Tab wraps to the order in force, and Shift+Tab reaches it by
 * itself. From anywhere else — the dialog itself included — Tab goes to the
 * first stop and Shift+Tab to the last.
 */
function tabThroughSortSheet(e: KeyboardEvent, stops: HTMLElement[], dialog: HTMLElement): void {
  const first = stops[0];
  const last = stops[stops.length - 1];
  const active = document.activeElement as HTMLElement | null;
  const inside = active !== null && active !== dialog && dialog.contains(active);
  const inList = inside && active.getAttribute('role') === 'option';
  if (e.shiftKey) {
    if (!inside || inList) {
      e.preventDefault();
      last.focus();
    }
  } else if (!inside) {
    e.preventDefault();
    first.focus();
  } else if (inList) {
    e.preventDefault();
    last.focus();
  } else if (active === last) {
    e.preventDefault();
    first.focus();
  }
}

/**
 * The sheet. Mount it always (closed, it renders nothing — not even on the
 * server, where ClientPortal renders null); `open` decides.
 */
export const SortSheet: React.FC<SortSheetProps> = ({ open, returnFocusRef, ...panel }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Every opening is a new panel (FilterSheet's idiom): a panel still
  // animating out is never revived by the next opening.
  const [session, setSession] = useState(0);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setSession((s) => s + 1);
  }
  // The page locked, Tab kept inside, Escape closes, and the focus back on the
  // opener the moment the sheet closes — not after its exit animation. The
  // lock is released before the focus goes back.
  useEffect(() => (open ? lockPageScroll() : undefined), [open]);
  // The tab stops: the order in force (the roving options' one stop) and
  // "Annuleren". The order in force takes the focus (the panel mounts in the
  // same commit as `open`; the frame is a margin for a portal a render later).
  useFocusTrap(dialogRef, open, panel.onClose, {
    scope: 'document',
    returnFocusRef,
    tabbable: (el) => el.tabIndex >= 0,
    initialFocus: (dialog) =>
      dialog.querySelector<HTMLElement>('[role="option"][aria-selected="true"]') ?? dialog,
    onTab: tabThroughSortSheet,
  });

  return (
    <ClientPortal>
      <AnimatePresence>
        {open && <SortSheetPanel key={`sort-sheet-${session}`} {...panel} dialogRef={dialogRef} />}
      </AnimatePresence>
    </ClientPortal>
  );
};
